import { getConsumptionsAndEmissionsWithIdentifiers } from '@oneaudi/feature-app-utils';
import type { Logger } from '@feature-hub/logger';
import type {
  VueFormatterFeatureServiceInterface,
  LocaleService,
} from './types/feature-service-types';
import type { Tile } from './context/ContentContext/contentTypes';

type WltpEntry = { formattedConsumption?: unknown; formattedEmission?: unknown } | undefined;
type WltpComplianceLogger = Pick<Logger, 'warn' | 'error'>;

type CaeMissingEvent = {
  event: 'CAE_MISSING';
  fa: typeof __FEATURE_APP_NAME__;
  carlineId: string;
  market: string;
  page: string;
  timestamp: string;
};

const hasEmptyStringKey = (wltpKey: unknown): boolean => {
  if (Array.isArray(wltpKey)) {
    return wltpKey.some((value) => value === '');
  }
  return wltpKey === '';
};

const hasRequiredValues = (entry: WltpEntry) => {
  const { formattedConsumption, formattedEmission } = entry || {};
  return Boolean(formattedConsumption && formattedEmission);
};

const getPagePath = (): string => {
  if (typeof window === 'undefined') return '';
  return window.location.pathname;
};

const buildCaeMissingMessage = (carlineId: string, localeService: LocaleService): string => {
  const payload: CaeMissingEvent = {
    event: 'CAE_MISSING',
    fa: __FEATURE_APP_NAME__,
    carlineId,
    market: localeService.market || localeService.countryCode || '',
    page: getPagePath(),
    timestamp: new Date().toISOString(),
  };
  return JSON.stringify(payload);
};

const logCaeMissing = (
  carlineId: string,
  localeService: LocaleService,
  logger?: WltpComplianceLogger,
): void => {
  const errorMsg = buildCaeMissingMessage(carlineId, localeService);
  if (typeof window !== 'undefined') console.error(errorMsg);
  logger?.error(errorMsg);
};

export const collectWltpKeys = (tiles: Tile[]): string[] => {
  const allKeys = tiles.flatMap((tile) => tile.wltpKeys ?? []).filter(Boolean);
  return [...new Set(allKeys)];
};

export const resolveWltpKeys = async (
  keys: string[],
  vueFormatterService: VueFormatterFeatureServiceInterface,
  localeService: LocaleService,
): Promise<Record<string, boolean>> => {
  const unique = [...new Set(keys.filter(Boolean))];
  if (!unique.length) return {};

  const result = await getConsumptionsAndEmissionsWithIdentifiers(
    unique,
    vueFormatterService,
    localeService,
  );

  const map: Record<string, boolean> = {};
  for (const key of unique) {
    map[key] = hasRequiredValues(result[key]);
  }
  return map;
};

export const isTileCompliant = (tile: Tile, complianceMap: Record<string, boolean>): boolean => {
  const keys = tile.wltpKeys ?? [];
  if (hasEmptyStringKey(keys)) return false;
  if (!keys.length) return true; // static tile — always compliant
  return keys.every((key) => complianceMap[key]);
};

export const getPerTileWltpCompliance = async (
  tiles: Tile[],
  vueFormatterService: VueFormatterFeatureServiceInterface,
  localeService: LocaleService,
  logger?: WltpComplianceLogger,
): Promise<boolean[]> => {
  const perTileCompliance = new Array<boolean>(tiles.length).fill(true);
  const tileKeysByIndex: string[][] = Array.from({ length: tiles.length }, () => []);
  const validKeyTileIndices: number[] = [];
  const allKeysSet = new Set<string>();

  tiles.forEach((tile, index) => {
    const rawKeys = tile.wltpKeys ?? [];

    if (hasEmptyStringKey(rawKeys)) {
      logCaeMissing('', localeService, logger);
      perTileCompliance[index] = false;
      return;
    }

    const tileKeys = rawKeys.filter(Boolean);
    tileKeysByIndex[index] = tileKeys;

    if (tileKeys.length > 0) {
      validKeyTileIndices.push(index);
      tileKeys.forEach((key) => allKeysSet.add(key));
    }
  });

  if (validKeyTileIndices.length === 0) {
    return perTileCompliance;
  }

  try {
    const allKeys = [...allKeysSet];
    const result = await getConsumptionsAndEmissionsWithIdentifiers(
      allKeys,
      vueFormatterService,
      localeService,
    );

    validKeyTileIndices.forEach((tileIndex) => {
      const tileKeys = tileKeysByIndex[tileIndex];
      let tileCompliance = true;

      tileKeys.forEach((key) => {
        if (!hasRequiredValues(result[key])) {
          tileCompliance = false;
          logCaeMissing(key, localeService, logger);
        }
      });

      perTileCompliance[tileIndex] = tileCompliance;
    });

    return perTileCompliance;
  } catch {
    validKeyTileIndices.forEach((tileIndex) => {
      tileKeysByIndex[tileIndex].forEach((key) => {
        logCaeMissing(key, localeService, logger);
      });
      perTileCompliance[tileIndex] = false;
    });

    return perTileCompliance;
  }
};
